/**
 * Utility functions for migrating and cleaning up persona tools
 * when MCP servers are renamed or removed
 */

import { log, warn } from './debug';

/**
 * Migrate persona tools when an MCP server name changes
 * @param {Array} personas - Array of persona objects
 * @param {string} oldServerName - Previous server name
 * @param {string} newServerName - New server name
 * @returns {Object} { personas: Array, affectedCount: number }
 */
export function migratePersonaTools(personas, oldServerName, newServerName) {
    if (!oldServerName || !newServerName || oldServerName === newServerName) {
        return { personas, affectedCount: 0 };
    }

    let affectedCount = 0;
    const oldPrefix = `mcp__${oldServerName}__`;
    const newPrefix = `mcp__${newServerName}__`;

    const migratedPersonas = personas.map((persona) => {
        const updatedTools = persona.tools.map((toolName) => {
            if (toolName.startsWith(oldPrefix)) {
                affectedCount++;
                const baseTool = toolName.substring(oldPrefix.length);
                const newToolName = `${newPrefix}${baseTool}`;
                log(`  ↻ Migrating: ${toolName} → ${newToolName}`);
                return newToolName;
            }
            return toolName;
        });

        // Only return modified persona if tools changed
        if (updatedTools.join(',') !== persona.tools.join(',')) {
            return { ...persona, tools: updatedTools };
        }
        return persona;
    });

    if (affectedCount > 0) {
        log(`✓ Migrated ${affectedCount} tool references from "${oldServerName}" to "${newServerName}"`);
    }

    return { personas: migratedPersonas, affectedCount };
}

/**
 * Remove stale/orphaned tool names from personas
 * (tools that no longer exist in availableTools)
 * @param {Array} personas - Array of persona objects
 * @param {Array} availableTools - Array of currently available tool objects
 * @param {Array} mandatoryTools - Array of mandatory tool names (always kept)
 * @returns {Object} { personas: Array, removedCount: number, staleTools: Array }
 */
export function cleanupStaleTools(personas, availableTools, mandatoryTools = []) {
    const validToolNames = new Set(availableTools.map((t) => t.name));
    const mandatorySet = new Set(mandatoryTools);

    let totalRemovedCount = 0;
    const allStaleTools = new Set();

    const cleanedPersonas = personas.map((persona) => {
        const beforeCount = persona.tools.length;

        const cleanedTools = persona.tools.filter((toolName) => {
            // Keep mandatory tools
            if (mandatorySet.has(toolName)) {
                return true;
            }

            // Keep valid tools
            if (validToolNames.has(toolName)) {
                return true;
            }

            // This is a stale tool
            allStaleTools.add(toolName);
            warn(`  ✗ Removing stale tool from "${persona.name}": ${toolName}`);
            return false;
        });

        const removedCount = beforeCount - cleanedTools.length;
        totalRemovedCount += removedCount;

        // Only return modified persona if tools changed
        if (removedCount > 0) {
            return { ...persona, tools: cleanedTools };
        }
        return persona;
    });

    if (totalRemovedCount > 0) {
        log(`✓ Cleaned up ${totalRemovedCount} stale tool references from ${personas.length} personas`);
    }

    return {
        personas: cleanedPersonas,
        removedCount: totalRemovedCount,
        staleTools: Array.from(allStaleTools)
    };
}

/**
 * Detect stale tools in a single persona
 * @param {Object} persona - Persona object
 * @param {Array} availableTools - Array of currently available tool objects
 * @param {Array} mandatoryTools - Array of mandatory tool names
 * @returns {Array} Array of stale tool names
 */
export function detectStaleTools(persona, availableTools, mandatoryTools = []) {
    const validToolNames = new Set(availableTools.map((t) => t.name));
    const mandatorySet = new Set(mandatoryTools);

    return persona.tools.filter((toolName) => {
        // Not stale if it's mandatory or valid
        if (mandatorySet.has(toolName) || validToolNames.has(toolName)) {
            return false;
        }

        // Only consider MCP tools as potentially stale
        // (Built-in tools might not be in availableTools yet)
        return toolName.startsWith('mcp__');
    });
}

/**
 * Get all personas that use tools from a specific MCP server
 * @param {Array} personas - Array of persona objects
 * @param {string} serverName - MCP server name
 * @returns {Array} Array of persona names
 */
export function getPersonasUsingServer(personas, serverName) {
    const prefix = `mcp__${serverName}__`;

    return personas.filter((persona) => persona.tools.some((tool) => tool.startsWith(prefix))).map((persona) => persona.name);
}
